import type { BillingInvoiceModel } from '@customerio/hydra-ui-api';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';

/** Invoice states that mean money is still owed. */
const OWING_STATUSES = ['Unpaid', 'Overdue'];

interface OrdwayInvoicesGate {
  /** When false the query is skipped — only Ordway accounts have these invoices. */
  enabled: boolean;
}

interface OrdwayInvoices {
  invoices: BillingInvoiceModel[] | undefined;
  /** True when any invoice is Unpaid or Overdue. False while loading, so warnings stay quiet. */
  hasUnpaidInvoices: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Note TanStack ignores `enabled`, so only call this when the query applies to the account. */
  refetch: () => void;
}

/**
 * The account's Ordway invoices, plus whether any is still owing.
 *
 * Shared so the billing-history table and the reactivate modal read one query rather than issuing
 * two under different keys.
 *
 * `hasUnpaidInvoices` is false until the query has resolved successfully. A warning derived from it
 * must not appear on a loading or errored read — that would tell an account it owes money on no
 * evidence.
 */
export function useOrdwayInvoices({ enabled }: OrdwayInvoicesGate): OrdwayInvoices {
  const { account } = useAuth();

  const { data, isLoading, isError, refetch } = useHydraUIAPIQuery({
    queryKey: ['billingGetReceipts', { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.billing.getReceipts({ params: { path: { account_id: String(account.id) } }, signal }),
    queryConfig: { enabled },
  });

  const invoices = data?.ordway_invoices;
  const hasUnpaidInvoices = !isError && (invoices ?? []).some((invoice) => OWING_STATUSES.includes(invoice.status));

  return {
    invoices,
    hasUnpaidInvoices,
    isLoading,
    isError,
    refetch: () => {
      void refetch();
    },
  };
}
